// Date and time utility functions
//
// Every display-facing timestamp in this app is shown in IST (Asia/Kolkata),
// regardless of the viewer's own machine/browser timezone — all backend
// timestamps are stored/emitted in UTC, and `.toLocaleString()`-style calls
// without an explicit timeZone silently render in whatever timezone the
// browser happens to be set to, which is wrong for a team that operates in
// IST. Every formatter below pins `timeZone: IST_TIME_ZONE` so this can't
// drift per-viewer.

export const IST_TIME_ZONE = 'Asia/Kolkata';

const toDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  return isNaN(d.getTime()) ? null : d;
};

/**
 * General-purpose IST formatter — pass any Intl.DateTimeFormatOptions
 * (dateStyle/timeStyle, or individual parts) and the timezone is always
 * forced to IST. Returns "-" for null/invalid input so callers don't need
 * their own fallback.
 */
export const formatIST = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const d = toDate(date);
  if (!d) return '-';
  return d.toLocaleString('en-IN', { timeZone: IST_TIME_ZONE, ...options });
};

/**
 * Format date to readable string
 */
export const formatDate = (
  date: Date | string,
  format: 'short' | 'long' | 'full' = 'short'
): string => {
  const optionsMap: Record<'short' | 'long' | 'full', Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
    full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
  };

  return formatIST(date, optionsMap[format]);
};

/**
 * Format time
 */
export const formatTime = (
  date: Date | string,
  includeSeconds: boolean = false
): string => {
  return formatIST(date, {
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' as const }),
  });
};

/**
 * Format date and time together
 */
export const formatDateTime = (
  date: Date | string,
  includeSeconds: boolean = false
): string => {
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  return `${formatDate(dateStr)} ${formatTime(dateStr, includeSeconds)}`;
};

/**
 * Get relative time (e.g., "2 hours ago")
 */
export const getRelativeTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

/**
 * Check if date is today
 */
export const isToday = (date: Date | string): boolean => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

/**
 * Check if date is in the past
 */
export const isPast = (date: Date | string): boolean => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d < new Date();
};

/**
 * Add days to date
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Get date range string
 */
export const getDateRangeString = (start: Date, end: Date): string => {
  return `${formatDate(start)} - ${formatDate(end)}`;
};

/**
 * Parse ISO date string
 */
export const parseISODate = (dateString: string): Date => {
  return new Date(dateString);
};
